import { useCallback, useEffect, useRef } from "react";
import type { Product, TaskId } from "../registry/types";

export interface TaskCompletionEvent {
  product: Product;
  taskId: TaskId;
}

export type TaskCompletionListener = (event: TaskCompletionEvent) => void;

/**
 * Imperative event channel for task-completion attempts (#3). Contextual
 * modals call `signal()` after their underlying mutation succeeds (e.g.
 * provider connect, billing setup) so the provider can refetch team data
 * + progress + re-evaluate predicates without polling.
 *
 * Roll-our-own pub-sub since the merchant app has no existing event bus.
 * Listeners stored in a ref'd Set so unsubscribe is O(1) and the hook
 * identity stays stable across renders.
 */
export function useTaskCompletionEvents(): {
  signal: (event: TaskCompletionEvent) => void;
  subscribe: (listener: TaskCompletionListener) => () => void;
} {
  const listenersRef = useRef<Set<TaskCompletionListener>>(new Set());

  const signal = useCallback((event: TaskCompletionEvent) => {
    for (const listener of listenersRef.current) {
      try {
        listener(event);
      } catch {
        // A bad listener shouldn't break the chain. The listener itself
        // is responsible for its own error reporting.
      }
    }
  }, []);

  const subscribe = useCallback((listener: TaskCompletionListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // Clear all listeners on unmount so a stale closure doesn't pin the
  // provider tree's state in tests or after a team switch.
  useEffect(
    () => () => {
      listenersRef.current.clear();
    },
    [],
  );

  return { signal, subscribe };
}
